import { PRICE_MIN, clampPrice } from '../payments/price-limits.js';
import { BundleType, TRIAL_TITLE } from '../bundles/types.js';
import type { BundleUpdateBody } from '../bundles/bundle-update.dto.js';
import { DEFAULT_CLAIM_WEEK, weekText } from '../schedule/week-grid.js';

/**
 * Claim-flow constants and derivations — a DECORATOR-FREE module (same rule as
 * `OFFER_DURATION_OPTIONS` in bundles/types): client components import values
 * from here; the class-validator DTOs live in `claim-page.dto.ts`, whose
 * decorators need reflect-metadata and crash in the browser.
 */

export const CLAIM_SESSION_DURATION = 60;
// 15, matching the platform's standard intro duration (the signup seeder's
// SEED_TRIAL_DURATION) — 20 isn't a duration the product uses anywhere.
export const CLAIM_TRIAL_DURATION = 15;
/** The claim asks no hour rate: a page goes live on this and the owner edits it in the card. */
const DEFAULT_CLAIM_HOUR_PRICE = 40;

/**
 * Every pack is an hour a call at 5% off the single hour. Prod, 2026-09-26: packs
 * of 4 and 8 are 130 of 157 live packs, and a pack session sells at a median 0.95
 * to 0.96 of the same creator's single hour.
 */
const CLAIM_PACK_DISCOUNT = 5;
const packTitle = (sessions: number) => (sessions === 1 ? 'One-hour call' : `${sessions} calls`);
function derivePackPrice(hourPrice: number, sessions: number): number {
  if (sessions === 1) return hourPrice;
  // 8 calls at the top hour rate would pass the cap ($1,140), so a pack is pulled into the range.
  return clampPrice(hourPrice * sessions * (1 - CLAIM_PACK_DISCOUNT / 100));
}

export const DEFAULT_CLAIM_TRIAL_PRICE = 15;

/** A new page's intro is 30 minutes, so its $15 is a real saving on the default hour ($20 of it). */
const DEFAULT_CLAIM_INTRO_MINUTES = 30;

/**
 * The intro is always sold below what its minutes cost at the creator's own hour (2026-11-03):
 * that is the crossed-out price, a real one, never an invented "was".
 */
export function introFullPrice(hourPrice: number, minutes: number): number {
  return Math.round((hourPrice * minutes) / 60);
}

/** The most an intro may cost and still be a discount on the hour, inside the intro's own cap. */
export function introMaxPrice(hourPrice: number, minutes: number): number {
  return Math.min(CLAIM_CATALOGUE_LIMITS.introPrice.max, introFullPrice(hourPrice, minutes) - 1);
}

/** What one-on-one hours sell for on Meetmy: the middle half of a year's paid hours. */
export const MARKET_HOUR_PRICE = { low: 35, high: 50 } as const;

/**
 * What a generated catalogue may be, from what sells (prod, 365 days to 2026-10-04: 60-minute
 * calls carry all the volume at a median $37.50 an hour; intros sell at $5/15 min, $15/30 min).
 */
export const CLAIM_CATALOGUE_LIMITS = {
  hourPrice: { min: 20, max: 150 },
  introPrice: { max: 30 },
  introMinutes: [15, 30],
  packSessions: [1, 4, 8],
  titleMax: 60,
} as const;

/** The choices a catalogue is built from: the generator's, or the defaults. */
export interface ClaimCatalogue {
  hourPrice: number;
  introMinutes: number;
  introPrice: number;
  /** Which packs to sell, by calls in each, and what each is called. */
  packs: { sessions: number; title?: string }[];
}

export const DEFAULT_CLAIM_CATALOGUE: ClaimCatalogue = {
  hourPrice: DEFAULT_CLAIM_HOUR_PRICE,
  introMinutes: DEFAULT_CLAIM_INTRO_MINUTES,
  introPrice: DEFAULT_CLAIM_TRIAL_PRICE,
  packs: [{ sessions: 1 }, { sessions: 4 }],
};

/** What a new page opens on, in words: the connector's tools and its help page say it alike. */
export const defaultsLine = () => {
  const { hourPrice, introMinutes, introPrice } = DEFAULT_CLAIM_CATALOGUE;
  return `an hour at $${hourPrice}, a ${introMinutes}-minute intro at $${introPrice} and ${weekText(DEFAULT_CLAIM_WEEK).join(', ')}`;
};

/**
 * The catalogue a claim goes live with, in the shape `POST /bundle` takes. Every value is
 * pulled inside `CLAIM_CATALOGUE_LIMITS`, so whatever a generator proposes is sellable;
 * an empty or unusable pack list falls back to the default packs.
 */
export function claimCatalogue(choice: ClaimCatalogue): BundleUpdateBody {
  const {
    hourPrice: hour,
    introPrice,
    introMinutes,
    packSessions,
    titleMax,
  } = CLAIM_CATALOGUE_LIMITS;
  const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, Math.round(v)));
  const hourPrice = clamp(choice.hourPrice, hour.min, hour.max);
  const minutes = (introMinutes as readonly number[]).includes(choice.introMinutes)
    ? choice.introMinutes
    : CLAIM_TRIAL_DURATION;
  const seen = new Set<number>();
  const packs = choice.packs.filter(
    (p) =>
      (packSessions as readonly number[]).includes(p.sessions) &&
      !seen.has(p.sessions) &&
      seen.add(p.sessions),
  );
  const sorted = [...(packs.length ? packs : DEFAULT_CLAIM_CATALOGUE.packs)].sort(
    (a, b) => a.sessions - b.sessions,
  );
  const biggest = sorted[sorted.length - 1].sessions;
  const full = introFullPrice(hourPrice, minutes);
  const intro = clamp(
    choice.introPrice,
    PRICE_MIN,
    Math.max(PRICE_MIN, introMaxPrice(hourPrice, minutes)),
  );
  return {
    bundles: sorted.map((p) => ({
      title: cleanTitle(p.title, titleMax) || packTitle(p.sessions),
      numberOfClasses: p.sessions,
      price: derivePackPrice(hourPrice, p.sessions),
      // The biggest pack is the deal, struck from the same calls bought one at a time.
      ...(p.sessions === biggest && p.sessions > 1
        ? { previousPrice: hourPrice * p.sessions }
        : {}),
      currency: 'USD',
      type: BundleType.ONE_ON_ONE,
      duration: CLAIM_SESSION_DURATION,
    })),
    trialBundle: {
      title: TRIAL_TITLE,
      numberOfClasses: 1,
      price: intro,
      // A 15-minute intro on a cheap hour has no room under it: then it is sold without one.
      ...(full > intro ? { previousPrice: full } : {}),
      currency: 'USD',
      type: BundleType.TRIAL,
      duration: minutes,
    },
  };
}

/** A written title, or nothing when it carries a price or a link: the card prints both itself. */
function cleanTitle(title: string | undefined, max: number): string | undefined {
  const line = title?.replace(/\s+/g, ' ').trim().slice(0, max);
  if (!line || /[$€£₽]|https?:|www\./i.test(line)) return undefined;
  return line;
}

/** What a creator chose for a new page; anything left out is the default. */
export interface PageChoices {
  hourPrice?: number;
  introPrice?: number;
  introMinutes?: number;
  packs?: number[];
}

/** What a new page sells: what the creator chose, else the defaults. The web preview and the claim read this one. */
export const catalogueFor = (choices: PageChoices): BundleUpdateBody =>
  claimCatalogue({
    hourPrice: choices.hourPrice ?? DEFAULT_CLAIM_CATALOGUE.hourPrice,
    introPrice: choices.introPrice ?? DEFAULT_CLAIM_CATALOGUE.introPrice,
    introMinutes: choices.introMinutes ?? DEFAULT_CLAIM_CATALOGUE.introMinutes,
    packs: choices.packs?.map((sessions) => ({ sessions })) ?? DEFAULT_CLAIM_CATALOGUE.packs,
  });

/** The catalogue a page goes live with when nothing was generated for it. */
export function defaultClaimProducts(): BundleUpdateBody {
  return claimCatalogue(DEFAULT_CLAIM_CATALOGUE);
}
